import { useEffect, useRef } from "react";
import { useUser } from "../auth/useUser";
import { useGlobalRunStore, runKey } from "../hooks/useGlobalRunStore";
import { useNotificationPrefs } from "../hooks/useNotificationPrefs";
import { useProducts, productName } from "../hooks/useProducts";
import { shouldNotifyBrowser, playCompletionChime } from "../lib/runNotify";

// RunCompletionNotifier (0027 P6): mounted at the App root; watches the
// global run store and, when a run completes, fires the opt-in off-site
// surfaces — a browser Notification (when the tab is hidden) and/or a sound
// cue. The on-site surface is the RunPill (P3) + recap (P4), so we don't pop
// an OS notification while the tab is focused. Fires at most once per run.
export function RunCompletionNotifier() {
  const { data: user } = useUser();
  const runs = useGlobalRunStore((s) => s.runs);
  const notificationsOnComplete = useNotificationPrefs((s) => s.notificationsOnComplete);
  const soundOnComplete = useNotificationPrefs((s) => s.soundOnComplete);
  const notifiedRef = useRef<Set<string>>(new Set());

  const productsQ = useProducts();

  useEffect(() => {
    if (!user) return;
    for (const r of Object.values(runs)) {
      if (r.snapshot.phase !== "complete") continue;
      const key = runKey(r);
      if (notifiedRef.current.has(key)) continue;

      const permission =
        typeof Notification !== "undefined" ? Notification.permission : "denied";
      const fireBrowser = shouldNotifyBrowser({
        prefEnabled: notificationsOnComplete,
        permission,
        documentHidden: typeof document !== "undefined" && document.hidden,
      });

      if (fireBrowser) {
        const name = productName(productsQ.data?.products, r.productId);
        try {
          const n = new Notification("Your spec is ready", {
            body: `"${name}" is ready to review.`,
            tag: key,
          });
          n.onclick = () => {
            window.focus();
            window.location.href = `/products/${r.productId}`;
            n.close();
          };
        } catch {
          // Notification construction can throw on some platforms — ignore.
        }
      }
      if (soundOnComplete) {
        playCompletionChime();
      }
      // Mark notified regardless of whether the browser popup fired (sound
      // path, or visible-tab) so we never double-fire for this run.
      notifiedRef.current.add(key);
    }
  }, [runs, user, notificationsOnComplete, soundOnComplete, productsQ.data]);

  return null;
}
